// Dossiers de licence (D-029) : le bureau suit chaque dossier de la saison
// (licence FFBB, médical, pièces demandées par le club, cotisation et son
// échéancier) ; les familles voient ce qui leur manque ; le coach voit qui est
// licenciée. Aucune pièce stockée : les médicales restent dans e-Licence (FFBB).
import {
  activeSeason, addLicenceItem, addPayments, archiveLicenceItem, clubDirectory, deletePayment, updatePayment, dossierPayments, feeOf,
  licenceItems, licencesOf, remindLicences, saveLicence, seasonLicences, seasonPayments, seasonPersonFees, seasonSurclassements,
  seasonTeamsFees, setPaymentStatus, setSurclassement, setTeamFee, updateSurclassement,
  type LicenceItem, type LicencePayment, type LicenceRow, type SurclassementRow, type TeamFee,
} from '../lib/api';
import {
  certificateValidUntil, euros, METHOD_LABEL, nextStatus, paymentTotals, rosterDossiers, splitPlan, STATUS_LABEL, type PaymentMethod,
} from '../domain/licences';
import {
  inForce, KIND_LABEL, KIND_RULE, STATUS_LABEL as SURC_STATUS, STATUS_ORDER as SURC_ORDER, type SurclassementKind, type SurclassementStatus,
} from '../domain/surclassement';
import { displayName } from '../domain/labels';
import { localDate } from '../domain/schedule';
import { shortDate } from './format';
import { type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast, armed } from './dom';
import { emptyGuide } from '../guide/empty';
import { settingsPage } from './nav';
import './accords.css';

const FFBB_LABEL = { a_faire: 'Licence à faire', envoyee: 'Licence envoyée', validee: 'Licence validée' } as const;
const MED_LABEL = { a_fournir: 'Médical à fournir', questionnaire: 'Questionnaire attesté', certificat: 'Certificat' } as const;
const SUGGESTED: [string, boolean][] = [
  ['Chèque de caution', false], ['Règlement intérieur signé', false], ['Autorisation parentale', true], ['Autorisation droit à l’image', false],
];

const today = () => localDate(new Date());
const plural = (n: number, one: string, many: string) => (n > 1 ? many : one);

const empty = (personId: string, seasonId: string, clubId: string): LicenceRow => ({
  person_id: personId, season_id: seasonId, club_id: clubId, ffbb_status: 'a_faire', ffbb_number: null,
  medical: 'a_fournir', certificate_on: null, items_done: [], fee_due_cents: null,
});

/** Écran du bureau : tous les dossiers de la saison. */
export async function renderLicences(root: HTMLElement, ctx: Ctx, filter: 'todo' | 'all' = 'todo'): Promise<void> {
  const club = ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id) ? ctx.club : null;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div>`); return; }
  const season = await activeSeason(club.id);
  if (!season) { mount(root, html`<div class="empty"><p class="empty-title">Ouvre d'abord la saison.</p><a class="btn btn-primary" href="#/club/equipes">Club › Équipes et coachs</a></div>`); return; }
  const [people, rowsList, items, payments, surcs, teams, teamFee] = await Promise.all([
    clubDirectory(club.id), seasonLicences(club.id, season.id), licenceItems(club.id), seasonPayments(club.id, season.id),
    seasonSurclassements(season.id).catch(() => [] as SurclassementRow[]),
    seasonTeamsFees(club.id, season.id).catch(() => [] as TeamFee[]),
    seasonPersonFees(club.id, season.id).catch(() => new Map<string, number>()),
  ]);
  const byPerson = new Map(rowsList.map(r => [r.person_id, r]));
  const day = today();
  const players = people.filter(p => p.teams.length);   // l'effectif de la saison
  const list = players.map(p => {
    const l = byPerson.get(p.person_id);
    return {
      p, l: { ...(l ?? empty(p.person_id, season.id, club.id)), team_fee: teamFee.get(p.person_id) ?? null }, opened: !!l, missing: l?.missing ?? [],
      pays: payments.filter(x => x.person_id === p.person_id),
    };
  });
  type Item = typeof list[number];
  const isDone = (x: Item) => x.opened && !x.missing.length;
  // Le même calcul que l'accueil du bureau (B3) : les dossiers de l'effectif de la saison.
  const sum = rosterDossiers(players.map(p => p.person_id), rowsList);
  const done = sum.complete;
  const toRemind = sum.incomplete;
  const shown = filter === 'todo' ? list.filter(x => !isDone(x)) : list;
  // Cotisations attendues : tout l'effectif (tarif d'équipe ou cotisation fixée), dossier ouvert ou non.
  const due = list.reduce((a, x) => a + (feeOf(x.l) ?? 0), 0);
  const tot = paymentTotals(due, payments, day);
  const nameOf = new Map(players.map(p => [p.person_id, displayName(p)]));
  const toCash = payments.filter(x => x.status === 'recu' && x.due_on <= day);
  const surcOf = (pid: string) => surcs.filter(x => x.person_id === pid);
  const surcList = [...surcs].sort((a, b) => SURC_ORDER.indexOf(a.status) - SURC_ORDER.indexOf(b.status)
    || (nameOf.get(a.person_id) ?? '').localeCompare(nameOf.get(b.person_id) ?? ''));
  const surcTodo = surcs.filter(x => x.status === 'demande' || x.status === 'recu').length;
  const surcChip = (x: SurclassementRow) => html`<span class="chip ${inForce(x, day) ? 'chip-ok' : x.status === 'refuse' ? 'chip-mute' : 'chip-ko'}">Surclassement ${x.category} · ${SURC_STATUS[x.status].toLowerCase()}</span>`;
  const reload = (f = filter) => renderLicences(root, ctx, f);
  // D-169 : les documents signés en ligne, « qui a signé quoi » (une ligne, rien si le club n'en demande pas).
  const docsLine = () => void import('../lib/accords-api').then(m => m.documentsBoard(club.id)).then(b => {
    const el = $(root, '#lic-docs');
    if (!el || !b.docs.length) return;
    const exp = b.docs.reduce((n, d) => n + d.expected, 0), got = b.docs.reduce((n, d) => n + d.signed, 0);
    mount(el, html`<a class="card card-row" href="#/licences/signatures" data-guide="lic-docs"><span class="play-thumb" aria-hidden="true"><span class="ti ti-doc"></span></span>
      <span class="card-main"><span class="card-title">Documents signés</span><span class="muted small">${got} / ${exp} signatures · ${b.docs.length} ${b.docs.length > 1 ? 'documents' : 'document'} en ligne</span></span><span class="occ-go" aria-hidden="true">›</span></a>`);
  }).catch(() => undefined);

  const row = (x: Item) => {
    const { p, l, opened, missing, pays } = x;
    const fee = feeOf(l);
    const t = paymentTotals(fee, pays, day);
    const lacking = items.filter(i => missing.includes(i.label)).length;   // selon la base (âge compris)
    return html`
      <li><button type="button" class="lic-row" data-person="${p.person_id}">
        <span class="card-main">
          <strong>${displayName(p)}</strong>
          <span class="muted small">${p.teams.join(', ')}${l.ffbb_number ? ` · n° ${l.ffbb_number}` : ''}</span>
          ${opened ? html`
            <span class="occ-chips">
              <span class="chip ${l.ffbb_status === 'validee' ? 'chip-ok' : 'chip-mute'}">${FFBB_LABEL[l.ffbb_status]}</span>
              <span class="chip ${l.medical === 'a_fournir' ? 'chip-ko' : 'chip-ok'}">${MED_LABEL[l.medical]}${l.certificate_on ? ` · ${shortDate(l.certificate_on)}` : ''}</span>
              ${items.length ? html`<span class="chip ${lacking ? 'chip-ko' : 'chip-ok'}">${lacking ? `${lacking} ${plural(lacking, 'pièce manquante', 'pièces manquantes')}` : 'Pièces ✓'}</span>` : ''}
              ${fee ? html`<span class="chip ${t.uncovered || t.late ? 'chip-ko' : t.received >= fee ? 'chip-ok' : 'chip-mute'}">Cotisation ${euros(t.received)} / ${euros(fee)}${pays.length > 1 ? ` · ${pays.length} fois` : ''}${t.late ? ' · retard' : ''}</span>` : ''}
              ${surcOf(p.person_id).map(surcChip)}
            </span>
            ${missing.length ? html`<span class="muted small">Manque : ${missing.join(' · ')}</span>` : ''}`
          : html`<span class="occ-chips"><span class="chip chip-mute">Dossier à ouvrir</span>${fee ? html`<span class="chip chip-soft">Cotisation ${euros(fee)}</span>` : ''}</span>`}
        </span>
        <span class="lic-state ${isDone(x) ? 'ok' : ''}" aria-label="${opened ? (missing.length ? 'incomplet' : 'complet') : 'à ouvrir'}">${!opened ? '+' : missing.length ? missing.length : '✓'}</span>
      </button></li>`;
  };

  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name} · saison ${season.name}</p><h2>Licences</h2>
        <span class="btn-row"><a class="btn btn-ghost btn-sm" href="#/licences/import" data-guide="lic-import">Importer des licenciés</a></span></header>
      <section class="card lic-sum">
        <span class="lic-n"><b>${done}</b> / ${list.length}</span>
        <span class="muted">dossiers complets</span>
        <span class="lic-bar" aria-hidden="true"><span style="width:${list.length ? Math.round((done / list.length) * 100) : 0}%"></span></span>
        ${toRemind ? html`<button type="button" class="btn btn-primary btn-sm" id="b-remind" data-guide="lic-remind">Relancer ${toRemind} ${plural(toRemind, 'famille', 'familles')}</button>` : ''}
        <p class="hint">Les pièces médicales restent dans e-Licence (FFBB), les autres au club : ici, on suit seulement ce qui est reçu.</p>
      </section>
      <div id="lic-docs"></div>
      ${due || payments.length ? html`
      <section class="card lic-cash">
        <span class="eyebrow">Cotisations</span>
        <div class="lic-kpis">
          <span><b>${euros(tot.cashed)}</b><small>encaissé</small></span>
          <span><b>${euros(tot.received - tot.cashed)}</b><small>reçu, à encaisser</small></span>
          <span><b>${euros(tot.planned - tot.received)}</b><small>attendu</small></span>
          <span class="${tot.uncovered ? 'ko' : ''}"><b>${euros(tot.uncovered)}</b><small>non échelonné</small></span>
        </div>
        ${tot.late ? html`<p class="lic-late">${euros(tot.late)} en retard sur les échéanciers</p>` : ''}
        ${toCash.length ? html`
          <p class="lic-sub">À porter à la banque (échéance arrivée)</p>
          <ul class="list lic-paylist">${toCash.map(x => html`
            <li class="lic-pay">
              <span class="card-main"><strong>${nameOf.get(x.person_id) ?? '—'}</strong><span class="muted small">${METHOD_LABEL[x.method]} · ${euros(x.amount_cents)} · ${shortDate(x.due_on)}</span></span>
              <button type="button" class="btn btn-ghost btn-sm" data-cash="${x.id}">Encaissé</button>
            </li>`)}</ul>` : ''}
      </section>` : ''}
      ${surcList.length ? html`
      <section class="card lic-surc">
        <span class="eyebrow">Surclassements${surcTodo ? ` · ${surcTodo} à traiter` : ''}</span>
        <ul class="list">${surcList.map(x => html`
          <li><button type="button" class="lic-row" data-surc-p="${x.person_id}" data-surc-c="${x.category}">
            <span class="card-main">
              <strong>${nameOf.get(x.person_id) ?? '—'} → ${x.category}</strong>
              <span class="muted small">${KIND_LABEL[x.kind]}${x.effective_on ? ` · en vigueur le ${shortDate(x.effective_on)}` : ''}</span>
            </span>
            <span class="chip ${inForce(x, day) ? 'chip-ok' : x.status === 'refuse' ? 'chip-mute' : 'chip-ko'}">${SURC_STATUS[x.status]}</span>
          </button></li>`)}</ul>
        <p class="hint">Demandés par les coachs. En vigueur dès le dépôt au comité (date d'effet), pour la saison seulement.</p>
      </section>` : ''}
      <div class="seg" role="tablist" aria-label="Filtre">
        <button type="button" role="tab" data-guide="lic-todo" data-f="todo" aria-selected="${String(filter === 'todo')}">À compléter (${list.length - done})</button>
        <button type="button" role="tab" data-f="all" aria-selected="${String(filter === 'all')}">Tous (${list.length})</button>
      </div>
      ${shown.length ? html`<ul class="list lic-list">${shown.map(row)}</ul>`
        : !list.length ? emptyGuide({ art: 'ti-id', guide: 'lic-empty', title: 'Tes licenciés en 5 minutes', tour: 'importer-licencies',
            text: 'L’export Excel de FBI, contrôlé ligne par ligne, importé tout ou rien : les dossiers de la saison se créent tout seuls.',
            alt: { label: 'Le tuto', href: '#/aide/importer-licencies' }, foot: 'Rien n’est enregistré avant la dernière étape ; un import s’annule pendant 7 jours.' })
        : html`<div class="empty"><p class="empty-title">${filter === 'todo' ? 'Tous les dossiers sont complets.' : 'Aucun effectif cette saison.'}</p></div>`}
    </section>`);

  $$<HTMLButtonElement>(root, '[data-f]').forEach(b => b.addEventListener('click', () => void reload(b.dataset.f as 'todo' | 'all')));
  $(root, '#b-remind')?.addEventListener('click', async () => {
    try {
      const r = await remindLicences(club.id, season.id);
      const parts = [
        r.sent ? `${r.sent} ${plural(r.sent, 'famille relancée', 'familles relancées')}` : '',
        r.already ? `${r.already} déjà ${plural(r.already, 'relancée', 'relancées')} aujourd’hui` : '',
        r.unreachable.length ? `sans compte dans l’app : ${r.unreachable.join(', ')} (à prévenir autrement)` : '',
      ].filter(Boolean);
      toast(parts.join(' · ') || 'Rien à relancer');
    } catch { toast('Relance impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-cash]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await setPaymentStatus(b.dataset.cash!, 'encaisse'); toast('Encaissement noté'); void reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-surc-p]')) {
    b.addEventListener('click', () => {
      const x = surcs.find(y => y.person_id === b.dataset.surcP && y.category === b.dataset.surcC)!;
      openSurclassement(nameOf.get(x.person_id) ?? '—', x, () => void reload());
    });
  }
  docsLine();
  for (const b of $$<HTMLButtonElement>(root, '[data-person]')) {
    b.addEventListener('click', () => {
      const it = list.find(x => x.p.person_id === b.dataset.person)!;
      openEditor(it.p, it.l, items, () => void reload());   // it.l porte le tarif de l'équipe
    });
  }
}

/** Réglages · Licences (D-139, maquette n1) : sortis de la page du quotidien, une page à part ouverte par la ⚙ de Licences ;
 *  deux rubriques repliables, Tarifs par équipe et Pièces demandées par le club. « Importer des licenciés » reste un bouton
 *  de la page Licences. */
export async function renderLicenceSettings(root: HTMLElement, ctx: Ctx, open?: string): Promise<void> {
  const club = ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id) ? ctx.club : null;
  if (!club) { mount(root, html`<section class="screen"><a class="back" href="#/licences">‹ Licences</a><div class="empty"><p class="empty-title">Réservé au bureau du club.</p></div></section>`); return; }
  const season = await activeSeason(club.id);
  if (!season) { mount(root, html`<section class="screen"><a class="back" href="#/licences">‹ Licences</a><div class="empty"><p class="empty-title">Ouvre d'abord la saison.</p><a class="btn btn-primary" href="#/club/equipes">Club › Équipes et coachs</a></div></section>`); return; }
  const [items, teams, docsSum] = await Promise.all([licenceItems(club.id), seasonTeamsFees(club.id, season.id).catch(() => [] as TeamFee[]),
    import('./documents').then(m => m.docsRubricSum(club.id, season.name)).catch(() => '')]);
  const priced = teams.filter(t => t.licence_fee_cents != null).length;
  const again = () => renderLicenceSettings(root, ctx);
  settingsPage(root, {
    key: 'licences', open: open ?? (teams.length && !priced ? 'tarifs' : null),
    intro: 'Ce qui se règle en début de saison. Le quotidien (dossiers, relances, cotisations) reste dans Licences.',
    rubrics: [
      { key: 'tarifs', icon: 'ti-card', title: 'Tarifs par équipe', sum: priced ? `${priced} / ${teams.length} équipes` : 'Aucun tarif', guide: 'lic-fees',
        body: (el) => feesBody(el, teams, again) },
      { key: 'pieces', icon: 'ti-doc', title: 'Pièces demandées par le club', sum: items.length ? items.map(i => i.label).join(' · ') : 'Aucune', guide: 'lic-items',
        body: (el) => itemsBody(el, club.id, items, again) },
      // D-169 : les documents du dossier signés en ligne (qui signe), et les accords des parents (rien à régler) ;
      // D-172 : rédiger un document dans l'app (un modèle ou une page blanche), 2 Mo par PDF, 5 documents actifs.
      { key: 'docs', icon: 'ak-i-sign', title: 'Documents à signer en ligne', sum: docsSum, guide: 'ak-docs-set',
        body: (el) => import('./documents').then(m => m.docsRubric(el, club.id, season.name, again)) },
      { key: 'accords', icon: 'ti-shield', title: 'Les accords des parents', sum: 'Photos et transport · demandés pour chaque mineur',
        body: (el) => import('./accords-club').then(m => m.accordsRubric(el)) },
    ],
    foot: html`<p class="hint">Les pièces médicales restent dans e-Licence (FFBB). L’import des licenciés reste un bouton de la page Licences.</p>`,
  }, again);
}

function feesBody(el: HTMLElement, teams: TeamFee[], again: () => void): void {
  mount(el, html`
    ${teams.length ? html`<ul class="lic-itemlist">${teams.map(tm => html`
      <li><span>${tm.name}${tm.category ? html` <span class="muted small">· ${tm.category}</span>` : ''}</span>
        <label class="lic-fee"><input type="number" inputmode="decimal" min="0" step="1" data-fee="${tm.id}" value="${tm.licence_fee_cents != null ? tm.licence_fee_cents / 100 : ''}" placeholder="—" aria-label="Tarif ${tm.name} (€)"> €</label></li>`)}</ul>`
      : html`<p class="hint">Crée d'abord les équipes de la saison (Club › Équipes).</p>`}
    <p class="hint">La cotisation de chacun suit le tarif de son équipe (dans plusieurs équipes : le plus élevé). Dans un dossier, une cotisation fixée à la main l'emporte (fratrie, réduction).</p>`);
  for (const inp of $$<HTMLInputElement>(el, '[data-fee]')) {
    inp.addEventListener('change', async () => {
      const v = inp.value.replace(',', '.').trim();
      const cents = v === '' ? null : Math.round(Number(v) * 100);
      if (cents != null && (Number.isNaN(cents) || cents < 0 || cents > 1000000)) { toast('Tarif invalide.', 'ko'); return; }
      try { await setTeamFee(inp.dataset.fee!, cents); toast(cents == null ? 'Tarif retiré' : `Tarif enregistré : ${euros(cents)}`); again(); }
      catch { toast('Enregistrement impossible.', 'ko'); }
    });
  }
}

function itemsBody(el: HTMLElement, clubId: string, items: LicenceItem[], again: () => void): void {
  const missingSuggestions = SUGGESTED.filter(([s]) => !items.some(i => i.label.toLowerCase() === s.toLowerCase()));
  mount(el, html`
    ${items.length ? html`<ul class="lic-itemlist">${items.map(i => html`
      <li><span>${i.label}${i.minors_only ? html` <span class="muted small">· mineurs</span>` : ''}</span>
        <button type="button" class="btn btn-ghost btn-sm" data-archive="${i.id}">Retirer</button></li>`)}</ul>` : ''}
    ${missingSuggestions.length ? html`<div class="occ-chips">${missingSuggestions.map(([s, minors]) => html`
      <button type="button" class="chip chip-mute chip-btn" data-suggest="${s}" data-minors="${minors ? '1' : ''}">+ ${s}</button>`)}</div>` : ''}
    <form class="lic-additem" id="f-item" novalidate>
      <input name="label" maxlength="60" placeholder="Autre pièce (ex. fiche d’inscription)" aria-label="Nouvelle pièce">
      <label class="lic-minors"><input type="checkbox" name="minors"> mineurs</label>
      <button class="btn btn-ghost btn-sm" type="submit">Ajouter</button>
    </form>
    <p class="hint">Chaque dossier liste ces pièces, à cocher à réception. « Mineurs » : demandée seulement aux moins de 18 ans (date de naissance).</p>`);
  const addItem = async (label: string, minors: boolean) => {
    if (label.trim().length < 2) return;
    try { await addLicenceItem(clubId, label, minors, (items.at(-1)?.position ?? 0) + 1); toast('Pièce ajoutée à tous les dossiers'); again(); }
    catch { toast('Cette pièce existe déjà, ou nom invalide.', 'ko'); }
  };
  for (const b of $$<HTMLButtonElement>(el, '[data-suggest]')) b.addEventListener('click', () => void addItem(b.dataset.suggest!, !!b.dataset.minors));
  $<HTMLFormElement>(el, '#f-item')!.addEventListener('submit', (e) => {
    e.preventDefault();
    const d = new FormData(e.target as HTMLFormElement);
    void addItem(String(d.get('label') ?? ''), d.get('minors') === 'on');
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-archive]')) {
    b.addEventListener('click', async () => {
      try { await archiveLicenceItem(b.dataset.archive!); toast('Pièce retirée (l’historique reste)'); again(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

function openEditor(p: { first_name: string; nickname: string | null }, l: LicenceRow, items: LicenceItem[], onClosed: () => void) {
  const draft = { ...l };
  let pays: LicencePayment[] = [];
  const seg = (name: string, value: string, opts: [string, string][]) => html`
    <div class="seg" role="radiogroup" data-seg="${name}">${opts.map(([v, label]) => html`<button type="button" role="radio" data-v="${v}" aria-pressed="${String(v === value)}">${label}</button>`)}</div>`;
  openSheet(`Licence · ${displayName(p)}`, html`
    <form class="form" id="f-lic" novalidate>
      <div class="field"><span>Licence FFBB</span>${seg('ffbb_status', l.ffbb_status, [['a_faire', 'À faire'], ['envoyee', 'Envoyée'], ['validee', 'Validée']])}</div>
      <label class="field"><span>Numéro de licence (facultatif)</span><input name="ffbb_number" maxlength="20" value="${l.ffbb_number ?? ''}" autocapitalize="characters"></label>
      <div class="field"><span>Médical</span>${seg('medical', l.medical, [['a_fournir', 'À fournir'], ['questionnaire', 'Questionnaire'], ['certificat', 'Certificat']])}</div>
      <label class="field" id="cert-date" ${l.medical === 'certificat' ? '' : 'hidden'}><span>Date du certificat</span><input name="certificate_on" type="date" value="${l.certificate_on ?? ''}"></label>
      <p class="hint">${l.certificate_on ? `Certificat valable jusqu'au ${shortDate(certificateValidUntil(l.certificate_on))} (3 saisons). ` : ''}Aucun document stocké : seulement l’état.</p>
      ${items.length ? html`<div class="field"><span>Pièces reçues</span><div class="lic-checks">${items.map(i => html`
        <label class="check"><input type="checkbox" name="item" value="${i.id}" ${l.items_done.includes(i.id) ? 'checked' : ''}><span><strong>${i.label}</strong>${i.minors_only ? html` <span class="muted small">mineurs</span>` : ''}</span></label>`)}</div></div>` : ''}
      <label class="field"><span>Cotisation due (€)</span><input name="due" type="number" inputmode="decimal" min="0" step="1" value="${l.fee_due_cents != null ? l.fee_due_cents / 100 : ''}" placeholder="${l.team_fee != null ? `${l.team_fee / 100} (tarif de l’équipe)` : ''}"></label>
      ${l.team_fee != null ? html`<p class="hint">Vide : tarif de l’équipe, ${euros(l.team_fee)}. À remplir seulement pour une cotisation différente (fratrie, réduction).</p>` : ''}
      <div class="field"><span>Échéancier</span><div id="pays"></div></div>
      <fieldset class="lic-plan">
        <legend>Ajouter au plan de paiement</legend>
        <div class="row-2">
          <label class="field"><span>Montant (€)</span><input name="p_amount" type="number" inputmode="decimal" min="1" step="1"></label>
          <label class="field"><span>En</span><select name="p_n">${[1, 2, 3, 4, 5, 6, 8, 10].map(n => html`<option value="${n}">${n === 1 ? '1 fois' : `${n} fois`}</option>`)}</select></label>
        </div>
        <label class="field"><span>Rythme</span><select name="p_every">${[[1, 'Tous les mois'], [2, 'Tous les 2 mois'], [3, 'Tous les 3 mois (trimestre)'], [4, 'Tous les 4 mois'], [6, 'Tous les 6 mois']].map(([v, t]) => html`<option value="${v}">${t}</option>`)}</select></label>
        <div class="row-2">
          <label class="field"><span>Moyen</span><select name="p_method">${(Object.keys(METHOD_LABEL) as PaymentMethod[]).map(m => html`<option value="${m}">${METHOD_LABEL[m]}</option>`)}</select></label>
          <label class="field"><span>1re échéance</span><input name="p_first" type="date" value="${today()}"></label>
        </div>
        <p class="hint" id="p-preview"></p>
        <label class="check"><input type="checkbox" name="p_received"><span><strong>Déjà remis au club</strong> <span class="muted small">ex. les chèques donnés à l’inscription</span></span></label>
        <button type="button" class="btn btn-ghost" id="b-plan">Ajouter au plan</button>
      </fieldset>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer le dossier</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-lic')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    for (const group of $$(sheet, '[data-seg]')) {
      group.querySelectorAll<HTMLButtonElement>('[data-v]').forEach(b => b.addEventListener('click', () => {
        (draft as unknown as Record<string, string>)[group.dataset.seg!] = b.dataset.v!;
        group.querySelectorAll('[data-v]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
        if (group.dataset.seg === 'medical') $(sheet, '#cert-date')!.hidden = b.dataset.v !== 'certificat';
      }));
    }
    const cents = (k: string) => { const v = String(new FormData(f).get(k) ?? '').replace(',', '.').trim(); return v === '' ? null : Math.round(Number(v) * 100); };
    /** L'état du formulaire, prêt à enregistrer ; null si invalide (message affiché). */
    const current = () => {
      const d = new FormData(f);
      const num = String(d.get('ffbb_number') ?? '').trim().toUpperCase();
      if (num && !/^[A-Z0-9]{4,20}$/.test(num)) { msg('Numéro de licence : lettres et chiffres seulement.'); return null; }
      const due = cents('due');
      if (due != null && (Number.isNaN(due) || due < 0)) { msg('Montant de cotisation invalide.'); return null; }
      return {
        person_id: draft.person_id, season_id: draft.season_id, club_id: draft.club_id,
        ffbb_status: draft.ffbb_status, medical: draft.medical, ffbb_number: num || null, fee_due_cents: due,
        items_done: d.getAll('item').map(String),
        certificate_on: draft.medical === 'certificat' ? (String(d.get('certificate_on') ?? '') || null) : null,
      };
    };
    let editing: string | null = null;   // l'échéance en cours de modification
    const renderPays = () => {
      const day = today();
      const t = paymentTotals(cents('due') ?? l.team_fee ?? null, pays, day);
      mount($(sheet, '#pays')!, html`
        ${pays.length ? html`<ul class="list lic-paylist">${pays.map(x => html`
          <li class="lic-pay ${x.status === 'attendu' && x.due_on < day ? 'late' : ''}">
            ${editing === x.id ? html`<span class="lic-pay-edit">
              <input type="number" inputmode="decimal" min="1" step="1" value="${x.amount_cents / 100}" aria-label="Montant (€)" data-ea>
              <input type="date" value="${x.due_on}" aria-label="Date de l’échéance" data-ed>
              <button type="button" class="btn btn-primary btn-sm" data-ok="${x.id}">OK</button></span>`
            : html`<button type="button" class="card-main lic-pay-main" data-edit="${x.id}" aria-label="Modifier l’échéance"><strong>${euros(x.amount_cents)}</strong><span class="muted small">${METHOD_LABEL[x.method]} · ${shortDate(x.due_on)}</span></button>`}
            <button type="button" class="chip ${x.status === 'attendu' ? 'chip-mute' : 'chip-ok'} chip-btn" data-st="${x.id}" aria-label="Statut : ${STATUS_LABEL[x.status]} (changer)">${STATUS_LABEL[x.status]}</button>
            <button type="button" class="sheet-x lic-del" data-del="${x.id}" aria-label="Supprimer l’échéance">×</button>
          </li>`)}</ul>` : html`<p class="hint">Aucun paiement prévu.</p>`}
        <p class="hint">${t.uncovered ? `Reste ${euros(t.uncovered)} à échelonner. ` : pays.length ? 'Cotisation entièrement prévue. ' : ''}${t.late ? `${euros(t.late)} en retard. ` : ''}${pays.length ? 'Un tap sur le statut : attendu → reçu → encaissé ; sur le montant : le modifier, ou décaler la date.' : ''}</p>`);
      for (const b of $$<HTMLButtonElement>(sheet, '[data-edit]')) b.addEventListener('click', () => { editing = b.dataset.edit!; renderPays(); });
      for (const b of $$<HTMLButtonElement>(sheet, '[data-ok]')) b.addEventListener('click', async () => {
        const row = b.closest('li')!;
        const a = Math.round(Number(String(row.querySelector<HTMLInputElement>('[data-ea]')!.value).replace(',', '.')) * 100);
        const due = row.querySelector<HTMLInputElement>('[data-ed]')!.value;
        if (!a || a <= 0 || !/^\d{4}-\d{2}-\d{2}$/.test(due)) { msg('Montant ou date invalide.'); return; }
        try { await updatePayment(b.dataset.ok!, { amount_cents: a, due_on: due }); editing = null; await refresh(); } catch { msg('Mise à jour impossible.'); }
      });
      const amount = f.elements.namedItem('p_amount') as HTMLInputElement;
      if (!amount.value && t.uncovered) amount.value = String(t.uncovered / 100);
      for (const b of $$<HTMLButtonElement>(sheet, '[data-st]')) {
        b.addEventListener('click', async () => {
          const x = pays.find(y => y.id === b.dataset.st)!;
          try { await setPaymentStatus(x.id, nextStatus(x.status)); await refresh(); } catch { msg('Mise à jour impossible.'); }
        });
      }
      for (const b of $$<HTMLButtonElement>(sheet, '[data-del]')) {
        b.addEventListener('click', async () => {
          if (!armed(b)) return;
          try { await deletePayment(b.dataset.del!); await refresh(); } catch { msg('Suppression impossible.'); }
        });
      }
    };
    const refresh = async () => { pays = await dossierPayments(l.person_id, l.season_id); renderPays(); preview(); };
    // Aperçu des échéances avant de les ajouter : « 3 × 60 € : 15 oct., 15 déc., 15 févr. ».
    const preview = () => {
      const d = new FormData(f), total = cents('p_amount'), first = String(d.get('p_first') ?? '');
      const plan = total && total > 0 && /^\d{4}-\d{2}-\d{2}$/.test(first) ? splitPlan(total, Number(d.get('p_n')), first, Number(d.get('p_every')) || 1) : [];
      $(sheet, '#p-preview')!.textContent = plan.length > 1 ? `${plan.length} échéances : ${plan.map(p => `${shortDate(p.due_on)} (${euros(p.amount_cents)})`).join(', ')}` : '';
    };
    for (const k of ['p_amount', 'p_n', 'p_every', 'p_first']) (f.elements.namedItem(k) as HTMLElement | null)?.addEventListener(k === 'p_amount' ? 'input' : 'change', preview);
    void refresh();
    $<HTMLInputElement>(f, '[name=due]')!.addEventListener('input', () => {
      (f.elements.namedItem('p_amount') as HTMLInputElement).value = '';
      renderPays();
    });

    $(f, '#b-plan')!.addEventListener('click', async () => {
      msg('');
      const total = cents('p_amount');
      const d = new FormData(f);
      const first = String(d.get('p_first') ?? '');
      if (!total || Number.isNaN(total) || total <= 0) { msg('Indique un montant.'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(first)) { msg('Indique la date de la 1re échéance.'); return; }
      const lic = current(); if (!lic) return;
      const status = d.get('p_received') === 'on' ? 'recu' as const : 'attendu' as const;
      try {
        await saveLicence(lic);   // le dossier doit exister pour porter l'échéancier
        await addPayments(splitPlan(total, Number(d.get('p_n')), first, Number(d.get('p_every')) || 1).map(x => ({
          ...x, person_id: l.person_id, season_id: l.season_id, method: d.get('p_method') as PaymentMethod, status,
        })));
        (f.elements.namedItem('p_amount') as HTMLInputElement).value = '';
        await refresh();
      } catch { msg('Échéancier impossible. Réessaie.'); }
    });

    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const lic = current(); if (!lic) return;
      try { await saveLicence(lic); toast('Dossier enregistré'); close(); }
      catch { msg('Enregistrement impossible. Réessaie.'); }
    });
  }, onClosed);
}

/** Le bureau fait avancer un surclassement : type, étape, date d'effet (D-030). */
function openSurclassement(name: string, x: SurclassementRow, onClosed: () => void) {
  // Club avec un responsable technique (D-093) : la demande attend l'avis du coach de la catégorie
  // d'accueil puis sa validation ; le bureau reprend le dossier ensuite (il est prévenu).
  if (x.status === 'technique') {
    openSheet(`Surclassement · ${name} → ${x.category}`, html`<p>Chez le responsable technique : l’avis du coach de la catégorie d’accueil, puis sa validation.</p>
      <p class="hint">Tu reprends le dossier (pièces, dépôt au comité) dès qu’il est validé : tu es prévenu.</p>`, undefined, onClosed);
    return;
  }
  let kind: SurclassementKind = x.kind, status: SurclassementStatus = x.status;
  const seg = (name: string, value: string, opts: [string, string][], cls = '') => html`
    <div class="seg ${cls}" role="radiogroup" data-seg="${name}">${opts.map(([v, label]) => html`<button type="button" role="radio" data-v="${v}" aria-pressed="${String(v === value)}">${label}</button>`)}</div>`;
  openSheet(`Surclassement · ${name} → ${x.category}`, html`
    <form class="form" id="f-surc" novalidate>
      <div class="field"><span>Type</span>${seg('kind', kind, (Object.keys(KIND_LABEL) as SurclassementKind[]).map(k => [k, KIND_LABEL[k]]), 'seg-wrap')}</div>
      <p class="hint" id="kind-rule">${KIND_RULE[kind]}</p>
      <div class="field"><span>Étape</span>${seg('status', status, SURC_ORDER.map(st => [st, SURC_STATUS[st]]), 'seg-wrap')}</div>
      <label class="field" id="eff" ${status === 'depose' || status === 'valide' ? '' : 'hidden'}><span>Date d'effet (dépôt au comité)</span><input name="effective_on" type="date" value="${x.effective_on ?? today()}"></label>
      <p class="hint">Valable pour la saison en cours seulement. Aucun certificat stocké ici : seulement l'étape.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
      <button type="button" class="btn btn-danger-ghost" id="b-del">Supprimer ce surclassement</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-surc')!;
    for (const group of $$(sheet, '[data-seg]')) {
      group.querySelectorAll<HTMLButtonElement>('[data-v]').forEach(b => b.addEventListener('click', () => {
        group.querySelectorAll('[data-v]').forEach(y => y.setAttribute('aria-pressed', String(y === b)));
        if (group.dataset.seg === 'kind') { kind = b.dataset.v as SurclassementKind; $(sheet, '#kind-rule')!.textContent = KIND_RULE[kind]; }
        else { status = b.dataset.v as SurclassementStatus; $(sheet, '#eff')!.hidden = !(status === 'depose' || status === 'valide'); }
      }));
    }
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const eff = String(new FormData(f).get('effective_on') ?? '') || null;
      try {
        await updateSurclassement(x.person_id, x.season_id, x.category, {
          kind, status, effective_on: status === 'depose' || status === 'valide' ? eff : null,
        });
        toast('Surclassement à jour'); close();
      } catch (err) {
        // D-133 : deux catégories au-dessus (ou plus), ce n'est jamais un surclassement simple.
        $(f, '.form-msg')!.textContent = /double surclassement/.test(String((err as Error)?.message ?? ''))
          ? 'Double surclassement (deux catégories au-dessus) : choisis « Régional ou national » ou « Exceptionnel ».'
          : 'Enregistrement impossible. Réessaie.';
      }
    });
    confirmTwice($<HTMLButtonElement>(sheet, '#b-del')!, 'Confirmer la suppression', async () => {
      try { await setSurclassement(x.person_id, x.season_id, x.category, false); toast('Surclassement supprimé'); close(); }
      catch { $(f, '.form-msg')!.textContent = 'Suppression impossible.'; }
    });
  }, onClosed);
}

/** Accueil des familles : ce qui manque au dossier de chacun des miens, et l'échéancier. */
export async function renderLicenceCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  const list = await licencesOf(ctx.people.map(p => p.id)).catch(() => [] as LicenceRow[]);
  const open = list.map(l => ({ l, p: ctx.people.find(x => x.id === l.person_id)! })).filter(x => x.p && x.l.missing?.length);
  if (!open.length) { mount(el, html``); return; }
  const pays = await Promise.all(open.map(x => dossierPayments(x.l.person_id, x.l.season_id).catch(() => [] as LicencePayment[])));
  const day = today();
  mount(el, html`${open.map(({ p, l }, i) => {
    const missing = l.missing!;
    const ps = pays[i] ?? [];
    const next = ps.find(x => x.status === 'attendu');
    const fee = feeOf(l);
    const t = paymentTotals(fee, ps, day);
    return html`
    <section class="card lic-card">
      <span class="eyebrow">Dossier de licence${p.user_id === ctx.uid ? '' : ` · ${displayName(p)}`}</span>
      <span class="card-title">Il manque ${missing.length} ${plural(missing.length, 'élément', 'éléments')}</span>
      <span class="muted small">${missing.join(' · ')}</span>
      ${fee ? html`<span class="small">Cotisation : ${euros(t.received)} remis sur ${euros(fee)}${next ? ` · prochaine échéance le ${shortDate(next.due_on)} (${euros(next.amount_cents)}, ${METHOD_LABEL[next.method].toLowerCase()})` : ''}</span>` : ''}
      <span class="hint">La licence et les pièces médicales se font dans e-Licence (lien reçu de la FFBB par e-mail) ; les autres pièces se remettent au club, qui met à jour ici.</span>
    </section>`;
  })}`);
}
